/**
 * TD Contact Form Component
 * 
 * A web component that handles form submission for the contact form.
 * Provides floating label functionality.
 */

// Check if the custom element is already defined to avoid errors
if (!customElements.get('td-contact-form')) {
  class TDContactForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      try {
        // Store references to DOM elements
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('button[type="submit"]');
        this.inputs = this.querySelectorAll('[data-td-input]') || [];
        
        // Only proceed if we have inputs to work with
        if (this.inputs.length > 0) {
          this.setupInputEventListeners();
        }
        
        // Set up submit button click event instead of form submit
        if (this.submitButton) {
          this.submitButton.addEventListener('click', this.handleSubmitClick.bind(this));
        }

      } catch (error) {
        console.error('Error initializing TD Contact Form:', error);
      }
    }

    /**
     * Set up event listeners for inputs
     */
    setupInputEventListeners() {
      // Input focus/blur events for floating labels
      this.inputs.forEach(input => {
        // Skip checkboxes and selects for floating label behavior
        if (input && input.type !== 'checkbox' && input.tagName !== 'SELECT') {
          input.addEventListener('focus', this.handleInputFocus.bind(this));
          input.addEventListener('blur', this.handleInputBlur.bind(this));
        }
      });
    }

    /**
     * Handle submit button click
     */
    async handleSubmitClick(event) {
      event.preventDefault();
      
      const isValidated = await this.validateCurrentFields();
      
      if (isValidated) {
        // If valid, add loading state and submit the form
        this.form.classList.add('td-loading');
        
        // Use setTimeout to ensure the loading state is applied
        setTimeout(() => {
          // Submit the form programmatically
          this.form.submit();
        }, 100);
      }
    }
    
    /**
     * Validate current fields
     * @returns {Promise<boolean>} - Promise that resolves to whether the fields are valid
     */
    validateCurrentFields() {
      return new Promise((validate) => {
        let validFlg = 0;
        const currentFormFields = this.querySelectorAll('[data-td-required]');
        
        if (currentFormFields.length === 0) {
          validate(true);
          return;
        }
        
        // Clear previous errors
        this.clearAllErrors();
        
        currentFormFields.forEach((field) => {
          if (this.validateIndividualField(field)) {
            validFlg += 1;
          }
        });
        
        if (validFlg === currentFormFields.length) {
          validate(true);
        } else if (validFlg < currentFormFields.length) {
          validate(false);
        }
      });
    }
    
    /**
     * Validate an individual field
     * @param {Element} field - The field to validate
     * @returns {boolean} - Whether the field is valid
     */
    validateIndividualField(field) {
      if (field.type === 'text') {
        if (field.value.trim() === '') {
          this.toggleError(field, true);
          return false;
        }
        this.toggleError(field, false);
        return true;
      } else if (field.type === 'email') {
        if (field.value.trim() === '') {
          this.toggleError(field, true);
          return false;
        }
        const emailPattern = /^[^@]+@[^@]+\.[^@]+$/;
        if (!emailPattern.test(field.value)) {
          this.toggleError(field, true);
          return false;
        }
        this.toggleError(field, false);
        return true;
      } else if (field.type === 'tel') {
        if (field.value.trim() === '') {
          this.toggleError(field, true);
          return false;
        }
        // Remove all non-numeric characters
        const digits = field.value.replace(/\D/g, '');
        // Check if we have 10 digits (US/Canada phone number)
        if (digits.length !== 10) {
          this.toggleError(field, true);
          return false;
        }
        this.toggleError(field, false);
        return true;
      } else if (field.type === 'checkbox') {
        // For checkboxes, check if it's checked
        if (!field.checked) {
          this.toggleError(field, true);
          return false;
        }
        this.toggleError(field, false);
        return true;
      } else if (field.tagName === 'TEXTAREA') {
        if (field.value.trim() === '') {
          this.toggleError(field, true);
          return false;
        }
        this.toggleError(field, false);
        return true;
      } else if (field.tagName === 'SELECT') {
        if (field.value.trim() === '') {
          this.toggleError(field, true);
          return false;
        }
        this.toggleError(field, false);
        return true;
      }
      
      return true;
    }
    
    /**
     * Toggle error state for a field
     * @param {Element} field - The field to toggle error for
     * @param {boolean} show - Whether to show or hide the error
     */
    toggleError(field, show) {
      const inputGroup = field.closest('[data-td-input-group]');
      if (!inputGroup) return;
      
      if (show) {
        inputGroup.classList.add('has-error');
      } else {
        inputGroup.classList.remove('has-error');
      }
    }
    
    /**
     * Clear all errors
     */
    clearAllErrors() {
      const errorGroups = this.querySelectorAll('.has-error');
      errorGroups.forEach(group => {
        group.classList.remove('has-error');
      });
    }

    /**
     * Handle input focus event
     * @param {Event} event - The focus event
     */
    handleInputFocus(event) {
      if (!event || !event.target) return;
      
      const inputGroup = event.target.closest('[data-td-input-group]');
      if (inputGroup) {
        inputGroup.classList.add('td-active');
      }
    }

    /**
     * Handle input blur event
     * @param {Event} event - The blur event
     */
    handleInputBlur(event) {
      if (!event || !event.target) return;
      
      const input = event.target;
      const inputGroup = input.closest('[data-td-input-group]');
      
      if (inputGroup && input.value === '') {
        inputGroup.classList.remove('td-active');
      }
    }
  }

  // Register the custom element
  customElements.define('td-contact-form', TDContactForm);
}
